import { useState } from "react";
import { CheckCircle2, AlertCircle, Info } from "lucide-react";

/**
 * QuickCheck — a lightweight interactive self-reflection question.
 *
 * Answers never leave the client. Nothing is profiled, transmitted,
 * or stored server-side. The component only exists to make the reader
 * pause for a moment and think.
 */
const TONE_ICON = { ok: CheckCircle2, warn: AlertCircle, info: Info };
const TONE_COLOR = { ok: "#10B981", warn: "#F59E0B", info: "#3B82F6" };

export default function QuickCheck({ question, options = [] }) {
    const [picked, setPicked] = useState(null);
    const picked_opt = picked !== null ? options[picked] : null;
    const Icon = picked_opt ? TONE_ICON[picked_opt.tone] || Info : null;

    return (
        <section className="qc-root" data-testid="quick-check">
            <style>{QC_CSS}</style>
            <div className="qc-eyebrow">QUICK CHECK</div>
            <h3 className="qc-question">{question}</h3>

            <div className="qc-options" role="radiogroup" aria-label={question}>
                {options.map((o, i) => (
                    <button
                        key={o.label}
                        type="button"
                        role="radio"
                        aria-checked={picked === i}
                        onClick={() => setPicked(i)}
                        className={`qc-opt ${picked === i ? "is-picked" : ""}`}
                        data-testid={`quick-check-opt-${i}`}
                    >
                        {o.label}
                    </button>
                ))}
            </div>

            {picked_opt && (
                <div className={`qc-verdict qc-verdict--${picked_opt.tone}`} data-testid="quick-check-verdict">
                    {Icon && <Icon size={16} strokeWidth={2.2} style={{ color: TONE_COLOR[picked_opt.tone] }}/>}
                    <span>{picked_opt.verdict}</span>
                </div>
            )}

            <p className="qc-note">Your answer is not saved or transmitted — this is just a moment to think.</p>
        </section>
    );
}

const QC_CSS = `
.qc-root { padding: 24px 26px; margin: 28px 0; background: #FFFFFF; border: 1px solid rgba(15,30,58,0.10); border-radius: 20px; }
.qc-eyebrow { font-size: 10.5px; letter-spacing: 0.24em; text-transform: uppercase; font-weight: 700; color: #C6A86B; margin-bottom: 10px; }
.qc-question { font-family: "Cormorant Garamond", ui-serif, Georgia, serif; font-size: 22px; font-weight: 500; letter-spacing: -0.012em; color: #0F1E3A; margin: 0 0 18px; line-height: 1.25; }
.qc-options { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 14px; }
.qc-opt { padding: 10px 16px; border-radius: 999px; border: 1px solid rgba(15,30,58,0.16); background: #FBF7EE; font-size: 13px; font-weight: 500; color: #0F1E3A; cursor: pointer; transition: background-color 180ms cubic-bezier(0.16,1,0.3,1), border-color 180ms cubic-bezier(0.16,1,0.3,1), transform 180ms cubic-bezier(0.16,1,0.3,1); }
.qc-opt:hover { border-color: #C6A86B; }
.qc-opt.is-picked { background: #0F1E3A; color: #F6F3EC; border-color: #0F1E3A; }
.qc-verdict { display: flex; align-items: flex-start; gap: 10px; padding: 12px 16px; border-radius: 12px; font-size: 14px; line-height: 1.55; margin-bottom: 10px; animation: qc-in 300ms cubic-bezier(0.16,1,0.3,1); }
.qc-verdict--ok   { background: rgba(16,185,129,0.08); color: #065F46; }
.qc-verdict--warn { background: rgba(245,158,11,0.08); color: #92400E; }
.qc-verdict--info { background: rgba(59,130,246,0.08); color: #1E40AF; }
.qc-note { font-size: 11.5px; color: #78716C; margin: 6px 0 0; }
@keyframes qc-in { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: none; } }
`;
